import { Injectable } from "@angular/core";
import { MatDialog } from "@angular/material/dialog";
import { Router } from "@angular/router";
import { SessionExpiredDialog } from "src/app/auth/session-expired-dialog/session-expired-dialog.component";
import { UserService } from "../../service/user-service";
import { MatSnackBar } from '@angular/material/snack-bar';
import { SnackBarUtil } from "../snackbar/snackbar-util";
import { SnackbarConstants } from "src/app/constants/snackbar-constants";


@Injectable({
    providedIn: 'root' // This makes the service available application-wide.
})
export class LocalStorageUtil {
    isDialogAlreadyOpened: boolean = false;
    constructor(private router: Router, private userService: UserService, private dialog: MatDialog,private snackBarUtil: SnackBarUtil, public snackBar: MatSnackBar,) { }

    ngOnInit(): void {
        this.userService.isAlreadyOpenedString$.subscribe(isOpened => {
            if (isOpened && isOpened.trim() && isOpened !== 'null') {
                this.isDialogAlreadyOpened = isOpened;
            }
        });
    }

    isAccessTokenPresentAndValid(): boolean {
        try {
            // Get the access token from local storage
            const JWTToken = localStorage.getItem('access_token');

            // Check if access token exists and is not null or empty
            if (JWTToken) {
                // Decode the access token
                const jwtData = JWTToken.split('.')[1];
                const decodedJwtJsonData = window.atob(jwtData);
                const decodedJwtData = JSON.parse(decodedJwtJsonData);
                // Check if decodedJwtData contains required fields
                if (
                    decodedJwtData &&
                    decodedJwtData.authorities &&
                    decodedJwtData.user_name &&
                    decodedJwtData.orgid
                ) {
                    return true; // Access token is present with non-null values
                }
            }
            else {
                console.error('Error while checking access token:');
                this.handleSessionInvalidError();
            }
        } catch (error) {
            console.error('Error while checking access token:', error);
            this.handleSessionInvalidError();

        }
        return false; // Access token is not present or doesn't have all required fields
    }

    isRefreshTokenPresentAndValid(): boolean {
        try {
            // Get the access token from local storage
            const JWTToken = localStorage.getItem('access_token');
            // Check if access token exists and is not null or empty
            if (JWTToken) {
                // Decode the access token
                const jwtData = JWTToken.split('.')[1];
                const decodedJwtJsonData = window.atob(jwtData);
                const decodedJwtData = JSON.parse(decodedJwtJsonData);
                // Check if decodedJwtData contains required fields
                if (decodedJwtData) {
                    return true; // Access token is present with non-null values
                }
            }
        } catch (error) {
            console.error('Error while checking access token:', error);
        }
        return false; // Access token is not present or doesn't have all required fields
    }

    checkIfAllMandatoryLocalStorageItemsArePresent(): boolean {
        try {
            // Check if access token is present and valid
            if (!this.isAccessTokenPresentAndValid()) {
                return false;
            }
            return true;
        } catch (error) {
            console.error('Error while checking mandatory local storage items:', error);
            return false; // Error occurred
        }
    }

    getAccessToken() {
        try {
            let JWTToken = localStorage.getItem('access_token');
            // Check if JWTToken is missing
            if (!JWTToken) {
                throw new Error("Access token not found in local storage");
            }
            // Decoding
            let jwtData = JWTToken.split('.')[1];
            let decodedJwtJsonData = window.atob(jwtData);
            let decodedJwtData = JSON.parse(decodedJwtJsonData);

            // Check if decodedJwtData is tampered or empty
            if (!decodedJwtData || !decodedJwtData.authorities || !decodedJwtData.user_name || !decodedJwtData.orgid) {
                throw new Error("Decoded JWT data is tampered or empty");
            }

            // Setting role-based Privileges
            let JWTPrivilegesArray = decodedJwtData.authorities;
            // Setting Username
            let loggedInUsername = decodedJwtData.user_name;
            let loggedInOrgID = decodedJwtData.orgid;
            let candidateid = decodedJwtData.candidateid;

            // Return the values
            return {
                'decodedJwt': decodedJwtData,
                'jwt': JWTToken,
                'jti': decodedJwtData.jti,
                'JWTPrivilegesArray': JWTPrivilegesArray,
                'loggedInUsername': loggedInUsername,
                'loggedInOrgID': loggedInOrgID,
                'candidateid': candidateid,
            };
        } catch (error) {
            console.error('Error while decoding access token:', error);
            this.snackBarUtil.triggerSnackBar(SnackbarConstants.ERROR_MESSAGE)
            // Handle the error
            this.handleSessionInvalidError();
        }
    }

    setAccessToken(accesstoken) {
        try {
            if (!accesstoken) {
                throw new Error("Invalid access token");
            }
            else {
                localStorage.setItem('access_token', accesstoken);
                this.userService.setSessionExpiredDialogOpened(false); // Update this.isDialogAlreadyOpened = false; 
            }
        } catch (error) {
            console.error('Error setting access token:', error.message);
            // Handle Error 
            this.handleSessionInvalidError();
        }
    }


    private exclusionKeyArrayForLocalStorage = ['viewPreferences'];
    private exclusionKeyArrayForSessionStorage = ['currentlanguage'];

    clearLocalStorageExceptExclusionKey() {
        // Create an object to store key-value pairs of excluded items
        let excludedItems: Map<string, string> = new Map<string, string>();
        // Fetch and store the excluded items
        this.exclusionKeyArrayForLocalStorage.forEach((key, index, array) => {
            const value = localStorage.getItem(key);
            if (value !== null) {
                excludedItems[key] = value;
            }
            // Check if it's the last element in the array
            if (index === array.length - 1) {
                // Clear localStorage
                localStorage.clear();
                // Restore excluded items
                Object.entries(excludedItems).forEach(([key, value]) => {
                    localStorage.setItem(key, value);
                });
            }
        });
    }

    clearSessionStorageExceptExclusionKey() {
        // Create an object to store key-value pairs of excluded items
        let excludedItems: Map<string, string> = new Map<string, string>();
        // Fetch and store the excluded items
        this.exclusionKeyArrayForSessionStorage.forEach((key, index, array) => {
            const value = sessionStorage.getItem(key);
            if (value !== null) {
                excludedItems[key] = value;
            }
            // Check if it's the last element in the array
            if (index === array.length - 1) {
                // Clear session
                sessionStorage.clear();
                // Restore excluded items
                Object.entries(excludedItems).forEach(([key, value]) => {
                    sessionStorage.setItem(key, value);
                });
            }
        });
    }


    clearLocalStorage() {
        this.clearLocalStorageExceptExclusionKey();
    }

    // file_contents for archieveDownload
    getFileContents() {
        return localStorage.getItem('file_contents');
    }
    setFileContents(bulkdownloadItems) {
        if (!bulkdownloadItems) {
            console.error('Error while getting get FileContents');
        }
        localStorage.setItem('jsessionid', bulkdownloadItems);
    }
    clearFileContents() {
        localStorage.removeItem('file_contents');
    }

    getViewPreferences() {
        try {
            let getViewPreference = localStorage.getItem('viewPreferences')
            if (getViewPreference) {
                let viewPreference = JSON.parse(window.atob(getViewPreference))
                return viewPreference;
            }
            else {
                console.error('Error while getting get ViewPreference:');
            }
        }
        catch (error) {
            console.error('Error while getting get ViewPreference:', error);

        }

    }

    setViewPreferences(viewPreferences) {
        if (!viewPreferences) {
            console.error('Error saving View Preference');
        }
        localStorage.setItem('viewPreferences', btoa(JSON.stringify(viewPreferences)));
    }

    clearViewPreferences() {
        localStorage.removeItem('viewPreferences');
    }
    //Invoked on login button click which inturns navigate to layout page
    getReferPage() {
        return localStorage.getItem('refer_page');
    }
    setReferPage(referPage) {
        if (!referPage) {
            console.error('Error saving refer Page');
        }
        localStorage.setItem('refer_page', referPage);
    }
    clearReferPage() {
        localStorage.removeItem('refer_page');
    }

    getDurationQuestionaire() {
        return JSON.parse(localStorage.getItem('duration'));
    }
    setDurationQuestionaire(duration) {
        if (!duration) {
            console.error('Error saving duration');
        }
        localStorage.setItem('duration', JSON.stringify(duration));
    }
    clearDurationQuestionaire() {
        localStorage.removeItem('duration');
    }

    handleSessionInvalidError() {
        // Close all open dialogs
        this.dialog.closeAll();
        // Programmatically reset file input if it's open
        const fileInput: HTMLInputElement | null = document.querySelector('input[type="file"]');
        if (fileInput) { fileInput.value = ''; }
        // Cleaning Session Storage
        this.clearSessionStorageExceptExclusionKey();
        // Clear the entire localStorage
        this.clearLocalStorageExceptExclusionKey();
        this.userService.setClearLocalStorage('clear');
        // Trigger Dialog
        // this.triggerSessionExpiredDialog(null); // Secure call dialog triggers are handled in JWT interceptor & Auth guard
    }

    triggerSessionExpiredDialog(cacheData) {
        if (!this.isDialogAlreadyOpened) {
            let dialogRef;
            if (!this.router.url.includes('careers/auth')) {
                 this.userService.setSessionExpiredDialogOpened(true); // Update this.isDialogAlreadyOpened = true;  
                if (cacheData) {
                    dialogRef = this.dialog.open(SessionExpiredDialog, {
                        disableClose: true,
                        data: cacheData
                    });
                }
                else {
                    dialogRef = this.dialog.open(SessionExpiredDialog, {
                        disableClose: true,
                    });
                }
                dialogRef.afterClosed().subscribe(data => {
                    this.userService.setSessionExpiredDialogOpened(false); // Update this.isDialogAlreadyOpened = false; 
                    this.dialog.closeAll();
                });
            }
        }
    }
}